import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { FeedbackBuffer } from '../feedback-buffer.js';
import { loadArtworkTexture } from '../artwork-texture.js';

// Shared-rig contract: same reasoning as echo.js - the light here IS the
// buffer's own history, and a rig would only pollute the loop.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'advect',
  family: 'shader',
  danceability: 0.70,
  requiresTier: 1,
  energyLevel: 0.82,
  bpmMin: 110,
  bpmMax: 180,
  movement: 0.85,
  tonality: 0.60,
  prefers: { gridStrength: [0.50, 1.0], lowEnd: [0.35, 1.0] },
  enabled: true
};

// Advect: a feedback loop where the image is CARRIED BY A VELOCITY FIELD
// instead of being transformed as a whole.
//
// The difference from echo.js is one line in the fragment shader. Echo reads
// the previous frame at a globally transformed position - every pixel gets the
// same zoom/rotation/offset - so its trails are radial or spiral and always
// regular. Advect reads it at a position dictated by a LOCAL field, so
// different parts of the image go different ways and the image TEARS.
//
// SEMI-LAGRANGIAN, and the sign matters:
//   vec2 src = uv - vel * uDt;   // walk BACKWARD up the current
// We do not push colour forward (which leaves holes and needs scatter-writes a
// fragment shader cannot do); we FETCH it from where it came from. Every output
// pixel gets exactly one well-defined value, which is why this is
// unconditionally stable. With a plus sign the image climbs the current
// instead of riding it and the whole thing is simply wrong.
//
// THE FIELD IS GEOMETRIC AND QUANTISED, NEVER ORGANIC. This is the character
// of the piece, and it was got wrong once already: a curl-of-noise field is
// divergence-free and beautiful and produces filaments, volutes and marbling -
// a contemplative register, which is not what this is for. Echo works because
// it is a VJ tool: blunt shapes, geometric repetition, doubling. So every
// field here is PIECEWISE CONSTANT over hard-edged zones - blocks, lanes,
// rings, sectors - with velocities quantised to integer multiples of a base
// speed. The discontinuity at a zone boundary is not a defect to be smoothed
// away; it is what tears the image, and it is the point.
//
// And the field CUTS on every beat. Nothing is interpolated between two
// configurations: the cut is the event.
//
// THE RENDER PASSES run inside the display quad's onBeforeRender, the one
// place a party-mode visualizer may legally touch the WebGLRenderer. See the
// echo.js header for the full reasoning; this file follows it exactly.

// --- the loop ---------------------------------------------------------------
// Short. A long persistence smears the tears back into a fluid mush; a short
// one leaves blunt echoes, which is the register this wants.
const DECAY_DEFAULT = 0.91;
const DECAY_MIN = 0.85;
const DECAY_QUIET = 0.88;     // bands.high moves the trail length between
const DECAY_LOUD = 0.94;      // these two, and no further
const DECAY_MAX = 0.945;      // never >= 1, whatever the governor does
const FLOOR_SUB = 0.0025;
const CEILING = 0.92;
const GOVERN_EVERY_FRAMES = 30;
const GOVERN_HIGH = 0.45;     // higher than echo's: this image is MEANT to be full
const GOVERN_RESUME = 0.35;
const GOVERN_LOW = 0.05;
const GOVERN_STEP = 0.01;
const LUM_SIZE = 8;

// --- the field --------------------------------------------------------------
const REGIME_ID = { blocks: 0, shear: 1, radial: 2, rotor: 3, zero: 4 };
const BLOCKS_X = 6, BLOCKS_Y = 4;
const SHEAR_LANES = 9;
const RADIAL_RINGS = 8;
const ROTOR_SECTORS = 12;

// Velocity is a small INTEGER number of steps of this base speed, in flow
// units per second, where flow space is the buffer with y in [-0.5,0.5] and x
// widened by the aspect.
const AMP_BASE = 0.18;
const AMP_LOW_SWING = 0.22;    // bands.low accelerates the current
const AMP_MAX = 0.55;
const BAND_TAU = 0.15;
// One beat in eight freezes the field completely. ZERO is listed as a regime,
// but a regime lasts a whole phrase and the brief also calls it "un temps
// entier" - a phrase of frozen image is a dead screen, a beat of it is the
// breath it is described as. So it lives on the beat axis, not the phrase
// axis; ?advmode=zero still pins it permanently for inspection.
const ZERO_BEAT_CHANCE = 0.125;

// Synthesised band envelopes, for when radiopi has not analysed the track.
// Without a timeline director.bands is all zeros and EVERY mapping here would
// sit at its floor - a current too slow to tear anything, the shortest trails,
// a visualizer that runs and visibly does nothing. Same trap gallery.js and
// ember.js hit, same cure, and the same STEP shape for the same reason: a
// trigger sets the envelope to a peak in one frame and it decays from there,
// so the attack clears any threshold at any frame rate instead of depending on
// how many frames a ramp happens to get.
const SYNTH_BASE = 0.10;
const SYNTH_DECAY = 1.9;
const SYNTH_PERIOD_LOW = 4.7;
const SYNTH_PERIOD_MID = 3.1;
const SYNTH_PERIOD_HIGH = 6.3;

// --- injection --------------------------------------------------------------
// Full-bleed, not a patch in the middle. The cover is the material the field
// tears up, so there has to be enough of it on screen to tear.
// 0.02, not 0.07. The continuous bed re-stamps the cover every frame, so its
// opacity is really a TIME CONSTANT: at 0.07 the static cover reasserted itself
// in a quarter of a second and fought the tearing, and the image never got
// further than a slightly nicked photograph. At 0.02 it takes about five times
// as long, which leaves the four beats between downbeat resets for the field to
// actually take the picture apart.
const ART_OPACITY = 0.02;      // continuous bed
const ART_RESET_OPACITY = 1.0; // on the downbeat: a clean image to destroy again
const ART_RESET_FRAMES = 3;
const SHAPE_LIFE_S = 0.10;     // brief - being torn apart is the point
const TRACK_FADE_S = 1.2;
const FADE_DECAY = 0.93;
const DROP_INVERT_BEATS = 2;

// Posterisation: five levels per channel with an ordered dither. The immediate
// VJ marker, and what stops the image reading as a photograph being smeared.
const POSTER_LEVELS = 5.0;

// Hue drift, very weak: the tearing already produces variety.
const HUE_DRIFT_PER_FRAME = 0.0008;

const TIER = {
  // Tier 0 is a live-downgrade refuge only - requiresTier keeps the
  // orchestrator from ever OFFERING advect there (the brief makes tier 0
  // unavailable), but the frame-time governor can drop the tier mid
  // composition and the rebuild has to land somewhere valid.
  0: { scale: 0.25, regimes: ['blocks', 'shear', 'radial'], poster: true },
  1: { scale: 0.25, regimes: ['blocks', 'shear', 'radial'], poster: true },
  2: { scale: 0.5, regimes: ['blocks', 'shear', 'radial', 'rotor'], poster: true }
};
const DEFAULT_TIER = 2;

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function lerp(a, b, t) { return a + (b - a) * t; }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}

// ---------------------------------------------------------------------------
// The field, as a GLSL chunk shared by the advection pass and by the ?advfield
// display. Sharing the source (and the uniform objects) is what makes the
// debug view trustworthy: it cannot drift out of step with the field that is
// actually moving the image.
//
// PRECISION. The file is mediump, per the party-mode default, with two
// deliberate exceptions marked highp:
//
//   1. the sampling position. An advection loop re-reads its own output at a
//      computed coordinate every frame, so a position error does not stay put -
//      it feeds back and integrates. mediump is genuinely fp16 on the TV (see
//      the TV note in lava.js and the project memory), whose 11-bit mantissa
//      quantises a uv to about 1/2048, and that quantisation biases the fetch
//      in a fixed direction per region. Verified rather than assumed: with the
//      current pinned to zero, so that any movement at all is position error,
//      the image drifts 1.2px in highp and 5.9px in mediump over 1.5s. The
//      switch is ?advmediump=1.
//   2. the zone hash. A hash needs a large multiply before its fract(), and
//      fp16 has no mantissa left to spare at that magnitude - the field would
//      collapse onto a handful of repeated velocities.
// ---------------------------------------------------------------------------
const FIELD_CHUNK = `
  uniform float uAspect;
  uniform int uRegime;
  uniform float uAmp;
  uniform float uInvert;
  uniform float uBeatSeed;    // re-rolled on every beat: the cut
  uniform float uZero;        // 1.0 = this beat is frozen
  uniform float uStill;       // bands.mid: the fraction of zones held at rest

  highp float h11(highp float n) {
    return fract(sin(n * 12.9898 + 4.1414) * 43758.5453);
  }

  // A zone's velocity: one of eight compass directions, at an INTEGER number
  // of speed steps. Quantised on both axes, so neighbouring zones never differ
  // by a little - they differ by a whole step or not at all, and the image
  // tears along the boundary instead of bending across it.
  vec2 zoneVel(highp float zi) {
    highp float s = zi * 7.13 + uBeatSeed * 3.77;
    if (h11(s + 41.3) < uStill) return vec2(0.0);   // some zones simply hold
    float mag = floor(h11(s) * 3.0) + 1.0;          // 1..3 steps
    float dir = floor(h11(s + 19.7) * 8.0) * 0.7853981634;
    return vec2(cos(dir), sin(dir)) * mag;
  }

  // A signed integer step, for the fields that need one number per zone.
  float zoneStep(highp float zi) {
    highp float s = zi * 5.31 + uBeatSeed * 2.19;
    if (h11(s + 63.1) < uStill) return 0.0;
    return floor(h11(s) * 7.0) - 3.0;               // -3..3
  }

  // BLOCKS: 6x4 cells, each with its own constant velocity. The most direct
  // statement of the whole idea - the frame is cut into a grid and each piece
  // is dragged somewhere else.
  vec2 fieldBlocks(highp vec2 p) {
    vec2 g = vec2(${BLOCKS_X}.0, ${BLOCKS_Y}.0);
    vec2 c = floor(clamp(p / vec2(uAspect, 1.0) + 0.5, 0.0, 0.9999) * g);
    return zoneVel(c.x + c.y * ${BLOCKS_X}.0);
  }

  // SHEAR: horizontal lanes. Speeds are integer multiples of the base, never
  // continuous, so the lanes slide in lockstep rather than gliding past one
  // another.
  vec2 fieldShear(highp vec2 p) {
    float lane = floor(clamp(p.y + 0.5, 0.0, 0.9999) * ${SHEAR_LANES}.0);
    return vec2(zoneStep(lane + 40.0), 0.0);
  }

  // RADIAL: eight concentric rings, each expanding or contracting at its own
  // integer rate, so the rings slide through one another and the image is cut
  // into annuli.
  vec2 fieldRadial(highp vec2 p) {
    float r = length(p);
    float ring = floor(clamp(r / 0.62, 0.0, 0.9999) * ${RADIAL_RINGS}.0);
    vec2 rad = r > 1e-4 ? p / r : vec2(0.0);
    return rad * zoneStep(ring + 80.0);
  }

  // ROTOR: twelve sectors, each a rigid rotation at its own angular rate, so
  // the sectors fan out of alignment with each other.
  vec2 fieldRotor(highp vec2 p) {
    float sec = floor(clamp(atan(p.y, p.x) / 6.2831853 + 0.5, 0.0, 0.9999) * ${ROTOR_SECTORS}.0);
    return vec2(-p.y, p.x) * zoneStep(sec + 120.0) * 1.6;
  }

  vec2 velocityAt(highp vec2 p) {
    if (uZero > 0.5) return vec2(0.0);
    // Branching on a UNIFORM: coherent across the whole draw, so only one
    // field is ever evaluated and this costs nothing.
    vec2 v;
    if (uRegime == 0) v = fieldBlocks(p);
    else if (uRegime == 1) v = fieldShear(p);
    else if (uRegime == 2) v = fieldRadial(p);
    else if (uRegime == 3) v = fieldRotor(p);
    else v = vec2(0.0);
    return v * uAmp * uInvert;
  }
`;

const fullscreenVertexShader = `
  precision mediump float;
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position.xy * 2.0, 0.0, 1.0);
  }
`;

// uPOS is the precision of the sampling position, swapped by ?advmediump=1 so
// the precision claim above can be demonstrated rather than asserted.
function advectFragmentShader(posPrecision) {
  return `
  precision mediump float;
  #define uPOS ${posPrecision}

  ${FIELD_CHUNK}

  uniform sampler2D uPrevious;
  uniform float uDt;
  uniform float uDecay;
  uniform float uSeed;
  uniform float uHueShift;

  varying vec2 vUv;

  void main() {
    uPOS vec2 uvh = vUv;
    uPOS vec2 p = (uvh - 0.5) * vec2(uAspect, 1.0);
    vec2 vel = velocityAt(p);
    // Back up the current. The divide undoes the aspect widening so the flow
    // is isotropic on screen rather than in uv.
    uPOS vec2 src = uvh - (vel / vec2(uAspect, 1.0)) * uDt;
    vec3 prev = texture2D(uPrevious, src).rgb;

    if (uHueShift != 0.0) {
      float c = cos(uHueShift), s = sin(uHueShift);
      const float a = 1.0 / 3.0;
      const float b = 0.5773502692;
      prev = mat3(
        c + (1.0 - c) * a, a * (1.0 - c) - b * s, a * (1.0 - c) + b * s,
        a * (1.0 - c) + b * s, c + (1.0 - c) * a, a * (1.0 - c) - b * s,
        a * (1.0 - c) - b * s, a * (1.0 - c) + b * s, c + (1.0 - c) * a
      ) * prev;
    }

    // STABILITY - the same three cumulative guards as echo.js, plus the
    // governor. Non-negotiable in any feedback loop: the moment effective gain
    // crosses 1 the image runs away to white.
    prev *= uDecay;
    prev -= vec3(${FLOOR_SUB.toFixed(4)});
    prev = max(prev, 0.0);
    prev = min(prev, vec3(${CEILING.toFixed(3)}));

    // Dither, +-0.5/255. Advection needs this MORE than Echo does: every frame
    // resamples the image at a non-integer position, so bilinear rounding error
    // compounds on top of the quantisation error Echo already had. Masked on
    // black for the reason echo.js documents at length - an unsigned target
    // truncates the negative half and the residue pools into a grey veil.
    vec2 fcw = mod(floor(gl_FragCoord.xy) + uSeed, 64.0);
    float dith = (fract(dot(fcw, vec2(0.7548776662, 0.5698402909))) - 0.5) / 255.0;
    float sig = step(3.5 / 255.0, max(prev.r, max(prev.g, prev.b)));

    gl_FragColor = vec4(prev + dith * sig, 1.0);
  }
`;
}

const shapeVertexShader = `
  precision mediump float;
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

// The beat shapes: bar, ring, triangle, cross. Analytic SDFs for the reason
// echo.js records - the chain runs antialias:false and the buffer is upscaled,
// so a raw geometric edge becomes a staircase the loop then recirculates - but
// the feather is ONE buffer pixel, not a falloff. These are meant to be blunt:
// a soft-edged shape reads as a glow, and glows are the fluid register.
const shapeFragmentShader = `
  precision mediump float;
  uniform vec3 uColor;
  uniform float uOpacity;
  uniform float uKind;    // 0 bar, 1 ring, 2 triangle, 3 cross
  uniform float uPx;      // one buffer pixel, in the quad's own [-1,1] units
  varying vec2 vUv;
  void main() {
    vec2 q = (vUv - 0.5) * 2.0;
    float d;
    if (uKind < 0.5) {
      vec2 b = abs(q) - vec2(0.95, 0.17);
      d = length(max(b, 0.0)) + min(max(b.x, b.y), 0.0);
    } else if (uKind < 1.5) {
      d = abs(length(q) - 0.70) - 0.17;
    } else if (uKind < 2.5) {
      vec2 a = vec2(abs(q.x), q.y);
      d = max(a.x * 0.866 + a.y * 0.5 - 0.42, -(a.y + 0.55));
    } else {
      vec2 b1 = abs(q) - vec2(0.92, 0.15);
      vec2 b2 = abs(q) - vec2(0.15, 0.92);
      d = min(length(max(b1, 0.0)) + min(max(b1.x, b1.y), 0.0),
              length(max(b2, 0.0)) + min(max(b2.x, b2.y), 0.0));
    }
    float a = 1.0 - smoothstep(0.0, max(uPx, 1e-4), d);
    if (a <= 0.0) discard;
    gl_FragColor = vec4(uColor, a * uOpacity);
  }
`;

// The display pass. No relief: a luminance-gradient emboss gives the streaks a
// thickness, and thickness is exactly the fluid register this is not. What is
// here instead is the posterisation.
function displayFragmentShader(fieldDebug, poster) {
  return `
  precision mediump float;

  ${fieldDebug ? FIELD_CHUNK : ''}

  uniform sampler2D uBuffer;
  varying vec2 vUv;

  ${fieldDebug ? `
  vec3 hue2rgb(float h) {
    vec3 k = fract(vec3(h) + vec3(0.0, 2.0 / 3.0, 1.0 / 3.0));
    return clamp(abs(k * 6.0 - 3.0) - 1.0, 0.0, 1.0);
  }` : ''}

  ${poster ? `
  // Ordered 4x4 Bayer, built recursively from the 2x2 - cheaper than a lookup
  // table and exact. Returns 0..15.
  float bayer4(vec2 fc) {
    vec2 p1 = mod(fc, 2.0);
    vec2 p2 = floor(0.5 * mod(fc, 4.0));
    return 4.0 * mix(mix(0.0, 2.0, p1.y), mix(3.0, 1.0, p1.y), p1.x)
         +       mix(mix(0.0, 2.0, p2.y), mix(3.0, 1.0, p2.y), p2.x);
  }` : ''}

  void main() {
    ${fieldDebug ? `
    {
      // Angle as hue, magnitude as brightness. The check to run BEFORE judging
      // anything about the image: the zones must read as FLAT areas of one
      // colour separated by hard edges. If this looks like a smooth gradient,
      // the register will stay fluid whatever else is done.
      vec2 p = (vUv - 0.5) * vec2(uAspect, 1.0);
      vec2 v = velocityAt(p);
      float m = length(v);
      float h = atan(v.y, v.x) / 6.2831853 + 0.5;
      gl_FragColor = vec4(hue2rgb(h) * clamp(m / 0.6, 0.0, 1.0), 1.0);
      return;
    }` : ''}

    vec3 col = texture2D(uBuffer, vUv).rgb;

    ${poster ? `
    // Five levels per channel. The ordered dither is what keeps a large flat
    // area from banding into hard steps while still reading as posterised.
    float steps = ${(POSTER_LEVELS - 1).toFixed(1)};
    float d = bayer4(floor(gl_FragCoord.xy)) / 16.0;
    col = floor(col * steps + d) / steps;` : ''}

    gl_FragColor = vec4(clamp(col, 0.0, 1.0), 1.0);
  }
`;
}

const lumFragmentShader = `
  precision mediump float;
  uniform sampler2D uBuffer;
  varying vec2 vUv;
  void main() {
    gl_FragColor = vec4(texture2D(uBuffer, vUv).rgb, 1.0);
  }
`;

export class AdvectVisualizer extends Visualizer {
  constructor(options) {
    super();
    const tier = options && options.qualityTier != null ? options.qualityTier : DEFAULT_TIER;
    this._tier = TIER[tier] ? tier : DEFAULT_TIER;
    const forced = urlParam('advmode');
    this._forcedRegime = forced && REGIME_ID[forced] !== undefined ? forced : null;
    const forcedDecay = parseFloat(urlParam('advdecay'));
    this._forcedDecay = Number.isFinite(forcedDecay) ? forcedDecay : null;
    this._fieldDebug = urlParam('advfield') === '1';
    this._forceImpulse = urlParam('advimpulse') === '1';
    this._mediumPos = urlParam('advmediump') === '1';
  }

  init(scene, camera) {
    this._scene = scene;
    const layout = TIER[this._tier];
    this._layout = layout;

    this._buffer = new FeedbackBuffer({ scale: layout.scale });

    // --- field uniforms, shared by the advect pass and the debug display ---
    this._fieldUniforms = {
      uAspect: { value: 1 },
      uRegime: { value: REGIME_ID[this._forcedRegime || layout.regimes[0]] },
      uAmp: { value: AMP_BASE },
      uInvert: { value: 1 },
      uBeatSeed: { value: 0 },
      uZero: { value: 0 },
      uStill: { value: 0.25 }
    };

    // --- advection pass ---
    this._advectUniforms = Object.assign({
      uPrevious: { value: null },
      uDt: { value: 1 / 60 },
      uDecay: { value: this._forcedDecay != null ? this._forcedDecay : DECAY_DEFAULT },
      uSeed: { value: 0 },
      uHueShift: { value: 0 }
    }, this._fieldUniforms);

    this._advectScene = new THREE.Scene();
    this._advectQuad = new THREE.Mesh(
      new THREE.PlaneGeometry(1, 1),
      new THREE.ShaderMaterial({
        uniforms: this._advectUniforms,
        vertexShader: fullscreenVertexShader,
        fragmentShader: advectFragmentShader(this._mediumPos ? 'mediump' : 'highp'),
        depthTest: false,
        depthWrite: false
      })
    );
    this._advectQuad.frustumCulled = false;
    this._advectScene.add(this._advectQuad);

    // --- injection pass (same target, drawn on top, no clear) ---
    this._injectScene = new THREE.Scene();
    this._ortho = new THREE.OrthographicCamera(-0.5, 0.5, 0.5, -0.5, 0, 10);
    this._ortho.position.z = 1;

    this._artTexture = null;
    this._artMesh = new THREE.Mesh(
      new THREE.PlaneGeometry(1, 1),
      new THREE.MeshBasicMaterial({
        transparent: true, opacity: ART_OPACITY, depthTest: false, depthWrite: false
      })
    );
    this._artMesh.visible = false;
    this._injectScene.add(this._artMesh);

    this._shape = this._makeShape();
    this._shape.visible = false;
    this._injectScene.add(this._shape);

    // --- governor downsample ---
    this._lumTarget = new THREE.WebGLRenderTarget(LUM_SIZE, LUM_SIZE, {
      minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter,
      depthBuffer: false, stencilBuffer: false
    });
    this._lumScene = new THREE.Scene();
    this._lumUniforms = { uBuffer: { value: null } };
    this._lumQuad = new THREE.Mesh(
      new THREE.PlaneGeometry(1, 1),
      new THREE.ShaderMaterial({
        uniforms: this._lumUniforms,
        vertexShader: fullscreenVertexShader,
        fragmentShader: lumFragmentShader,
        depthTest: false, depthWrite: false
      })
    );
    this._lumQuad.frustumCulled = false;
    this._lumScene.add(this._lumQuad);
    this._lumPixels = new Uint8Array(LUM_SIZE * LUM_SIZE * 4);

    // --- display quad in the MAIN scene, before the shared bloom ---
    this._displayUniforms = Object.assign(
      { uBuffer: { value: null } },
      this._fieldDebug ? this._fieldUniforms : {}
    );
    this._displayMesh = new THREE.Mesh(
      new THREE.PlaneGeometry(1, 1),
      new THREE.ShaderMaterial({
        uniforms: this._displayUniforms,
        vertexShader: fullscreenVertexShader,
        fragmentShader: displayFragmentShader(this._fieldDebug, layout.poster),
        depthTest: false,
        depthWrite: false
      })
    );
    this._displayMesh.frustumCulled = false;
    this._displayMesh.renderOrder = -1000;
    const self = this;
    this._displayMesh.onBeforeRender = function (renderer) { self._runPasses(renderer); };
    scene.add(this._displayMesh);

    // --- state ---
    this._time = 0;
    this._paused = false;
    this._decay = this._forcedDecay != null ? this._forcedDecay : DECAY_DEFAULT;
    this._decayCeiling = DECAY_MAX;
    this._governorHot = false;
    this._frameCount = 0;
    this._lastLum = 0;
    this._regimeIndex = 0;
    this._regimeName = this._forcedRegime || layout.regimes[0];
    this._lastPhraseIndex = null;
    this._lastBeatIndex = null;
    this._lastBarIndex = null;
    this._lowSmooth = 0.5;
    this._midSmooth = 0.5;
    this._highSmooth = 0.5;
    this._synth = { low: SYNTH_BASE, mid: SYNTH_BASE, high: SYNTH_BASE };
    this._synthPhase = { low: null, mid: null, high: null };
    this._hueAngle = 0;
    this._hueDir = 1;
    this._palette = [new THREE.Color(0x39a0ff), new THREE.Color(0xff3ba7), new THREE.Color(0xffd23b)];
    this._lastPaletteRef = undefined;
    this._lastTrackId = undefined;
    this._lastArtworkUrl = undefined;
    this._lastMode = null;
    this._bpm = null;
    this._coldStart = true;
    this._nextSeedAt = 0;
    this._fadeUntil = -1;
    this._fading = false;
    this._shapeUntil = -1;
    this._invertUntil = -1;
    this._artResetFrames = 0;
  }

  _makeShape() {
    const mat = new THREE.ShaderMaterial({
      uniforms: {
        uColor: { value: new THREE.Color(0xffffff) },
        uOpacity: { value: 1 },
        uKind: { value: 0 },
        uPx: { value: 0.02 }
      },
      vertexShader: shapeVertexShader,
      fragmentShader: shapeFragmentShader,
      transparent: true, depthTest: false, depthWrite: false
    });
    const mesh = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), mat);
    mesh.frustumCulled = false;
    return mesh;
  }

  // Same 8x8 downsample + readback governor as echo.js, same hysteresis.
  //
  // It owns a CEILING, not the value. bands.high owns the value (the brief puts
  // trail length on the highs), and when the governor also wrote the value
  // directly the two fought: the band mapping runs every frame and simply
  // overwrote the governor's correction on the next one, so its low-end step
  // had no measurable effect at all over ten minutes. Lowering a ceiling that
  // the band mapping is clamped to cannot be overwritten, and still makes
  // divergence impossible whatever the highs ask for.
  _measureLuminance(renderer) {
    this._lumUniforms.uBuffer.value = this._buffer.write.texture;
    renderer.setRenderTarget(this._lumTarget);
    renderer.render(this._lumScene, this._ortho);
    renderer.readRenderTargetPixels(this._lumTarget, 0, 0, LUM_SIZE, LUM_SIZE, this._lumPixels);
    let sum = 0;
    for (let i = 0; i < this._lumPixels.length; i += 4) {
      sum += 0.299 * this._lumPixels[i] + 0.587 * this._lumPixels[i + 1] + 0.114 * this._lumPixels[i + 2];
    }
    const lum = sum / (255 * LUM_SIZE * LUM_SIZE);
    this._lastLum = lum;

    if (lum > GOVERN_HIGH) this._governorHot = true;
    if (this._governorHot) {
      if (lum > GOVERN_RESUME) {
        this._decayCeiling = Math.max(DECAY_MIN, this._decayCeiling - GOVERN_STEP);
        this._decay = Math.min(this._decay, this._decayCeiling);
      } else {
        this._governorHot = false;
      }
    } else if (lum < GOVERN_LOW && this._decayCeiling < DECAY_MAX) {
      this._decayCeiling = Math.min(DECAY_MAX, this._decayCeiling + GOVERN_STEP);
    }
  }

  _runPasses(renderer) {
    const rebuilt = this._buffer.ensureSize(renderer);
    if (rebuilt) {
      this._displayUniforms.uBuffer.value = this._buffer.read.texture;
      const aspect = this._buffer.width / Math.max(1, this._buffer.height);
      this._ortho.left = -0.5 * aspect;
      this._ortho.right = 0.5 * aspect;
      this._ortho.updateProjectionMatrix();
      this._fieldUniforms.uAspect.value = aspect;
      // Full bleed: a square of side = aspect covers the whole frame and is
      // cropped top and bottom, rather than letterboxed into the middle.
      this._artMesh.scale.set(aspect, aspect, 1);
    }
    if (this._paused) {
      // Frozen: the advection pass does not run at all and the display quad
      // re-shows the last buffer untouched.
      this._displayUniforms.uBuffer.value = this._buffer.read.texture;
      return;
    }

    const prevTarget = renderer.getRenderTarget();
    const prevAutoClear = renderer.autoClear;

    // 1. advection: read -> write
    this._advectUniforms.uPrevious.value = this._buffer.read.texture;
    // Written here, not in update(): this line runs after update() every frame
    // and would otherwise silently override the fade (echo.js hit exactly this).
    this._advectUniforms.uDecay.value = this._fading ? FADE_DECAY : this._decay;
    this._advectUniforms.uSeed.value = (this._frameCount * 13.13) % 64;
    renderer.setRenderTarget(this._buffer.write);
    renderer.autoClear = false;   // the quad overwrites every pixel
    renderer.render(this._advectScene, this._ortho);

    // 2. injection, on top, into the same target. The downbeat reset lives
    //    here because it is counted in FRAMES, and this is the function that
    //    runs once per frame.
    if (this._artResetFrames > 0) {
      this._artMesh.material.opacity = ART_RESET_OPACITY;
      this._artResetFrames--;
    } else {
      this._artMesh.material.opacity = ART_OPACITY;
    }
    renderer.render(this._injectScene, this._ortho);

    // 3. governor cadence - asleep during a track fade, or it would read the
    //    fading buffer as "too dark" and hand the next track a hot loop.
    this._frameCount++;
    if (!this._fading && this._frameCount % GOVERN_EVERY_FRAMES === 0) this._measureLuminance(renderer);

    renderer.setRenderTarget(prevTarget);
    renderer.autoClear = prevAutoClear;

    // 4. swap; the freshly written frame is what the main scene displays
    this._buffer.swap();
    this._displayUniforms.uBuffer.value = this._buffer.read.texture;
  }

  // The three envelopes the rest of the file reads: the real ones when radiopi
  // has analysed the track, the synthesised ones when it has not.
  // director.hasReliableGrid is exactly "a timeline exists", which is exactly
  // when director.bands carries real values - so it is the test, rather than
  // sniffing the numbers.
  _effectiveBands(profile, director) {
    if (director && director.hasReliableGrid && director.bands) return director.bands;
    return this._synth;
  }

  _stepSynthBands(deltaTime, profile) {
    const k = Math.exp(-SYNTH_DECAY * deltaTime);
    const sy = this._synth;
    sy.low = SYNTH_BASE + (sy.low - SYNTH_BASE) * k;
    sy.mid = SYNTH_BASE + (sy.mid - SYNTH_BASE) * k;
    sy.high = SYNTH_BASE + (sy.high - SYNTH_BASE) * k;
    // A trigger is a phase WRAP: one unambiguous event per cycle, no width to
    // tune, and the step itself is the attack.
    const fired = (key, phase) => {
      const prev = this._synthPhase[key];
      this._synthPhase[key] = phase;
      return prev != null && phase < prev;
    };
    const bar = profile.barPhase;
    const beat = profile.beatPhase;
    if (bar != null && beat != null) {
      if (fired('low', bar)) sy.low = 1.0;
      if (fired('mid', (bar + 0.5) % 1)) sy.mid = 0.85;
      if (fired('high', (beat + 0.5) % 1)) sy.high = 0.75;
      return;
    }
    const t = this._time;
    if (fired('low', (t / SYNTH_PERIOD_LOW) % 1)) sy.low = 1.0;
    if (fired('mid', (t / SYNTH_PERIOD_MID) % 1)) sy.mid = 0.85;
    if (fired('high', (t / SYNTH_PERIOD_HIGH) % 1)) sy.high = 0.75;
  }

  _applyPalette(palette) {
    const vivid = (palette && palette.vividColors && palette.vividColors.length)
      ? palette.vividColors : [0x39a0ff, 0xff3ba7, 0xffd23b];
    for (let i = 0; i < 3; i++) this._palette[i].set(vivid[i % vivid.length]);
    const hsl = { h: 0, s: 0, l: 0 };
    this._palette[0].getHSL(hsl);
    this._hueDir = hsl.h >= 0.5 ? -1 : 1;
  }

  // THE CUT. A new seed gives every zone of every field a new velocity on the
  // same frame, with nothing interpolated. This is the event the whole piece is
  // built on, which is why it is one line and fires on the beat.
  _cutField() {
    this._fieldUniforms.uBeatSeed.value = (this._fieldUniforms.uBeatSeed.value + 7.31) % 64;
    this._fieldUniforms.uZero.value =
      (this._forcedRegime === 'zero' || Math.random() < ZERO_BEAT_CHANCE) ? 1 : 0;
  }

  _spawnShape() {
    const aspect = this._fieldUniforms.uAspect.value;
    const m = this._shape.material.uniforms;
    m.uKind.value = Math.floor(Math.random() * 4);
    m.uColor.value.copy(this._palette[Math.floor(Math.random() * 3)]);
    const s = lerp(0.22, 0.62, Math.random());
    this._shape.scale.set(s, s, 1);
    // Eighths of a turn, not a free angle: the shapes belong to the same
    // quantised world as the field.
    this._shape.rotation.z = Math.floor(Math.random() * 8) * (Math.PI / 4);
    this._shape.position.set(
      (Math.random() - 0.5) * aspect * 0.8,
      (Math.random() - 0.5) * 0.8,
      0
    );
    // The feather, one buffer pixel, expressed in the quad's own [-1,1] units -
    // which is why it depends on the quad's scale.
    m.uPx.value = 2 / Math.max(1, this._buffer.height * s);
    this._shape.visible = true;
    this._shapeUntil = this._time + SHAPE_LIFE_S;
  }

  update(deltaTime, profile) {
    this._paused = profile.playbackState !== 'PLAYING';
    if (this._paused) return;
    const dt = Math.min(deltaTime, 0.05);
    this._time += deltaTime;
    this._advectUniforms.uDt.value = dt;

    const director = profile.director;
    this._stepSynthBands(dt, profile);
    const bands = this._effectiveBands(profile, director);
    const low = bands && bands.low != null ? clamp01(bands.low) : 0.5;
    const mid = bands && bands.mid != null ? clamp01(bands.mid) : 0.5;
    const high = bands && bands.high != null ? clamp01(bands.high) : 0.5;
    const k = 1 - Math.exp(-dt / BAND_TAU);
    this._lowSmooth += (low - this._lowSmooth) * k;
    this._midSmooth += (mid - this._midSmooth) * k;
    this._highSmooth += (high - this._highSmooth) * k;
    this._bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || null;

    if (profile.artworkPalette !== this._lastPaletteRef) {
      this._lastPaletteRef = profile.artworkPalette;
      this._applyPalette(profile.artworkPalette);
    }

    // Track change: 1.2s fade toward black, never a clear.
    if (profile.trackId !== this._lastTrackId) {
      const first = this._lastTrackId === undefined;
      this._lastTrackId = profile.trackId;
      if (!first) this._fadeUntil = this._time + TRACK_FADE_S;
    }
    const fading = this._time < this._fadeUntil;
    this._fading = fading;

    if (profile.artworkUrl !== this._lastArtworkUrl) {
      this._lastArtworkUrl = profile.artworkUrl;
      if (profile.artworkUrl) {
        const self = this;
        loadArtworkTexture(profile.artworkUrl, { wrap: THREE.ClampToEdgeWrapping, mipmaps: false },
          function (tex) {
            if (self._artTexture) self._artTexture.dispose();
            self._artTexture = tex;
            self._artMesh.material.map = tex;
            self._artMesh.material.needsUpdate = true;
          },
          function () {});
      }
    }

    // --- musical events ---
    const beatIndex = profile.beatIndex;
    if (this._coldStart && !fading && this._time >= this._nextSeedAt) {
      // Until a real beat arrives (the first poll takes seconds) nothing cuts
      // and the screen reads as dead. Self-seed meanwhile.
      this._nextSeedAt = this._time + 0.5;
      this._cutField();
      this._spawnShape();
    }
    if (beatIndex != null && this._lastBeatIndex != null && beatIndex !== this._lastBeatIndex && !fading) {
      this._coldStart = false;
      // EVERY BEAT: the field cuts to a new configuration, and a blunt shape
      // lands to be torn up by it.
      this._cutField();
      this._spawnShape();
      // THE BAR: the cover comes back at full opacity. This is what gives the
      // piece its structure - a clean image every four beats that then
      // disintegrates over the next four, instead of an endless smear.
      const barIndex = profile.barIndex;
      if (barIndex != null && barIndex !== this._lastBarIndex) {
        this._artResetFrames = ART_RESET_FRAMES;
      }
      this._lastBarIndex = profile.barIndex;
    } else if (this._lastBarIndex === null) {
      this._lastBarIndex = profile.barIndex;
    }
    this._lastBeatIndex = beatIndex;
    if (this._forceImpulse && this._frameCount % 60 === 0) this._cutField();

    // Phrase: change the TYPE of field. The configuration cuts every beat; the
    // kind of geometry it cuts to changes on the phrase.
    const phraseIndex = profile.phraseIndex;
    if (this._forcedRegime == null
      && phraseIndex != null && this._lastPhraseIndex != null && phraseIndex !== this._lastPhraseIndex) {
      const regimes = this._layout.regimes;
      this._regimeIndex = (this._regimeIndex + 1) % regimes.length;
      this._regimeName = regimes[this._regimeIndex];
      this._fieldUniforms.uRegime.value = REGIME_ID[this._regimeName];
    }
    this._lastPhraseIndex = phraseIndex;

    // Drop: the whole current reverses for two beats and everything flows back.
    // Taken from the director's own segment name so it works with the real
    // analysis timeline AND with the synthetic envelope.
    const mode = director ? director.mode : null;
    if (mode === 'drop' && this._lastMode !== 'drop') {
      const spb = this._bpm ? 60 / this._bpm : 0.5;
      this._invertUntil = this._time + DROP_INVERT_BEATS * spb;
    }
    this._lastMode = mode;
    // Instant, like everything else in this field: the inversion IS a cut.
    this._fieldUniforms.uInvert.value = this._time < this._invertUntil ? -1 : 1;

    // Retire injected content.
    if (this._time >= this._shapeUntil) this._shape.visible = false;
    this._artMesh.visible = !!this._artTexture && !fading;
    if (fading) this._shape.visible = false;

    // --- band mapping ---
    const u = this._fieldUniforms;
    // low  -> how far a zone travels per beat
    u.uAmp.value = Math.min(AMP_MAX, AMP_BASE + this._lowSmooth * AMP_LOW_SWING);
    // mid  -> how much of the frame is TORN rather than held. At rest most
    //         zones freeze and only a few slabs move, which is what makes the
    //         moving ones read as deliberate; on a mid hit the whole frame goes.
    u.uStill.value = lerp(0.55, 0.05, this._midSmooth);
    // high -> trail length, under the governor's ceiling
    if (this._forcedDecay == null) {
      const target = Math.min(lerp(DECAY_QUIET, DECAY_LOUD, this._highSmooth), this._decayCeiling);
      this._decay += (target - this._decay) * Math.min(1, dt * 1.5);
      this._decay = Math.max(DECAY_MIN, Math.min(this._decayCeiling, this._decay));
    }

    this._hueAngle = HUE_DRIFT_PER_FRAME * this._hueDir * Math.PI * 2;
    this._advectUniforms.uHueShift.value = this._hueAngle;
  }

  dispose() {
    this._scene.remove(this._displayMesh);
    this._displayMesh.geometry.dispose();
    this._displayMesh.material.dispose();
    this._advectQuad.geometry.dispose();
    this._advectQuad.material.dispose();
    for (const m of [this._artMesh, this._shape]) {
      m.geometry.dispose();
      m.material.dispose();
    }
    this._lumQuad.geometry.dispose();
    this._lumQuad.material.dispose();
    this._lumTarget.dispose();
    this._buffer.dispose();
    if (this._artTexture) this._artTexture.dispose();
  }
}
